// File: src/components/DocumentTemplates/MOA.js
import React, { useState, useRef } from "react";
import { useReactToPrint } from "react-to-print";
import DocumentLayout from "../DocumentLayout";

const MOA = () => {
    const [companyName, setCompanyName] = useState("Company Name Limited");
    const [registeredOffice, setRegisteredOffice] = useState(
        "123 Business Street, City, State, ZIP"
    );
    const [companyObjects, setCompanyObjects] = useState([
        "To carry on the business of software development and IT services",
        "To provide consulting services in technology and business management",
        "To develop and market software products and applications",
    ]);
    const [shareCapital, setShareCapital] = useState("1,000,000");
    const [liability, setLiability] = useState(
        "The liability of members is limited"
    );
    const [selectedTemplate, setSelectedTemplate] = useState("1");

    const componentRef = useRef();
    const handlePrint = useReactToPrint({
        content: () => componentRef.current,
        documentTitle: `${companyName}-MOA`,
    });

    const addObject = () => setCompanyObjects([...companyObjects, ""]);
    const updateObject = (i, v) => {
        const updated = [...companyObjects];
        updated[i] = v;
        setCompanyObjects(updated);
    };
    const removeObject = (i) =>
        setCompanyObjects(companyObjects.filter((_, idx) => idx !== i));

    // Different Templates
    const renderTemplate = () => {
        switch (selectedTemplate) {
            case "1": // Classic Legal
                return (
                    <div
                        ref={componentRef}
                        className="bg-white p-10 shadow-lg border-4 border-gray-700 max-w-3xl mx-auto"
                    >
                        <h2 className="text-2xl font-bold text-center mb-2 underline">
                            MEMORANDUM OF ASSOCIATION
                        </h2>
                        <p className="text-center text-gray-700 mb-6">OF</p>
                        <h1 className="text-3xl font-extrabold text-green-800 text-center mb-8 uppercase">
                            {companyName}
                        </h1>

                        <p className="mb-4">
                            <b>Registered Office:</b> {registeredOffice}
                        </p>
                        <p className="mb-4">
                            <b>Share Capital:</b> {shareCapital}
                        </p>
                        <p className="mb-4">
                            <b>Liability:</b> {liability}
                        </p>

                        <h3 className="font-semibold mt-6 mb-2">Objects of the Company:</h3>
                        <ol className="list-decimal list-inside space-y-2">
                            {companyObjects.map((obj, i) => (
                                <li key={i}>{obj}</li>
                            ))}
                        </ol>

                        <div className="mt-12 flex justify-between">
                            <div>
                                <p className="text-sm">_________________________</p>
                                <p className="text-sm">Director</p>
                            </div>
                            <p className="text-sm">Dated: {new Date().toDateString()}</p>
                        </div>
                    </div>
                );

            case "2": // Modern Clean
                return (
                    <div
                        ref={componentRef}
                        className="bg-white p-10 shadow-xl rounded-lg border max-w-3xl mx-auto"
                    >
                        <div className="text-center mb-8">
                            <h2 className="text-2xl font-bold tracking-wide text-gray-900">
                                Memorandum of Association
                            </h2>
                            <p className="text-sm text-gray-600">Incorporated under company law</p>
                        </div>

                        <h1 className="text-3xl font-bold text-green-700 text-center mb-6">
                            {companyName}
                        </h1>

                        <div className="space-y-3 mb-6">
                            <p>
                                <b>Registered Office:</b> {registeredOffice}
                            </p>
                            <p>
                                <b>Share Capital:</b> {shareCapital}
                            </p>
                            <p>
                                <b>Liability:</b> {liability}
                            </p>
                        </div>

                        <div className="mb-6">
                            <h3 className="font-semibold text-lg mb-2">Objects:</h3>
                            <ul className="list-disc list-inside space-y-1">
                                {companyObjects.map((obj, i) => (
                                    <li key={i}>{obj}</li>
                                ))}
                            </ul>
                        </div>

                        <div className="border-t pt-6 flex justify-between items-center">
                            <div>
                                <p className="text-sm">Authorized Signatory</p>
                            </div>
                            <div className="text-right">
                                <p className="text-xs">Issued: {new Date().toLocaleDateString()}</p>
                            </div>
                        </div>
                    </div>
                );

            case "3": // Decorative Header
                return (
                    <div
                        ref={componentRef}
                        className="bg-white p-8 shadow-lg border max-w-3xl mx-auto relative overflow-hidden"
                    >
                        <div className="bg-gradient-to-r from-green-700 to-emerald-600 text-white p-6 rounded-t">
                            <h2 className="text-2xl font-bold text-center">
                                MEMORANDUM OF ASSOCIATION
                            </h2>
                        </div>

                        <div className="p-6">
                            <h1 className="text-3xl font-bold text-center text-green-700 mb-6">
                                {companyName}
                            </h1>

                            <p className="mb-4">
                                <b>Registered Office:</b> {registeredOffice}
                            </p>
                            <p className="mb-4">
                                <b>Share Capital:</b> {shareCapital}
                            </p>
                            <p className="mb-4">
                                <b>Liability:</b> {liability}
                            </p>

                            <h3 className="font-semibold mb-2">Objects:</h3>
                            <ol className="list-decimal list-inside space-y-1">
                                {companyObjects.map((obj, i) => (
                                    <li key={i}>{obj}</li>
                                ))}
                            </ol>

                            <div className="mt-12 flex justify-between">
                                <div>
                                    <p className="text-sm">_________________________</p>
                                    <p className="text-xs">Director</p>
                                </div>
                                <p className="text-xs">Issued: {new Date().toLocaleDateString()}</p>
                            </div>
                        </div>
                    </div>
                );

            default:
                return null;
        }
    };

    return (

        <div className="space-y-6 bg-slate-50 p-6">
            {/* Inputs */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                    <label className="block text-sm font-medium">Company Name</label>
                    <input
                        type="text"
                        value={companyName}
                        onChange={(e) => setCompanyName(e.target.value)}
                        className="w-full p-2 border rounded"
                    />
                </div>
                <div>
                    <label className="block text-sm font-medium">Registered Office</label>
                    <input
                        type="text"
                        value={registeredOffice}
                        onChange={(e) => setRegisteredOffice(e.target.value)}
                        className="w-full p-2 border rounded"
                    />
                </div>
                <div>
                    <label className="block text-sm font-medium">Share Capital</label>
                    <input
                        type="text"
                        value={shareCapital}
                        onChange={(e) => setShareCapital(e.target.value)}
                        className="w-full p-2 border rounded"
                    />
                </div>
                <div>
                    <label className="block text-sm font-medium">Liability Clause</label>
                    <textarea
                        value={liability}
                        onChange={(e) => setLiability(e.target.value)}
                        rows="3"
                        className="w-full p-2 border rounded"
                    />
                </div>
                <div>
                    <label className="block text-sm font-medium">Template Style</label>
                    <select
                        value={selectedTemplate}
                        onChange={(e) => setSelectedTemplate(e.target.value)}
                        className="w-full p-2 border rounded"
                    >
                        <option value="1">Classic Legal</option>
                        <option value="2">Modern Clean</option>
                        <option value="3">Decorative Header</option>
                    </select>
                </div>
            </div>

            {/* Objects */}
            <div>
                <div className="flex justify-between items-center mb-2">
                    <label className="block text-sm font-medium">Objects</label>
                    <button
                        onClick={addObject}
                        className="px-3 py-1 bg-green-600 text-white text-sm rounded hover:bg-green-700"
                    >
                        Add
                    </button>
                </div>
                <div className="space-y-2">
                    {companyObjects.map((obj, index) => (
                        <div key={index} className="flex items-center space-x-2">
                            <input
                                type="text"
                                value={obj}
                                onChange={(e) => updateObject(index, e.target.value)}
                                className="flex-1 p-2 border rounded"
                            />
                            <button
                                onClick={() => removeObject(index)}
                                className="p-2 text-red-600 hover:bg-red-50 rounded"
                            >
                                ×
                            </button>
                        </div>
                    ))}
                </div>
            </div>
            <DocumentLayout
                title="Memorandum of Association"
                description="Company memorandum of association document"
                onSave={() => alert("MOA saved!")}
                onDownload={handlePrint}
                onPrint={handlePrint}
            >
                {/* Preview */}
                <div className="mt-8 border rounded bg-gray-50">{renderTemplate()}</div>
            </DocumentLayout>
        </div>
    );
};

export default MOA;
